import { useEffect, useMemo, useRef, useState } from "react";
import { WheelColumn, type WheelHandle } from "../shared/WheelColumn";
import css from "./CustomTimeModal.module.css";

/**
 * Sub-modal triggered by «НЕ СТАНДАРТНЫЙ СЛОТ» button.
 *
 * Per screen-03 эталон (`.cyber-prompt.time-prompt`):
 *   - centred floating panel with pink→purple gradient border
 *   - dark inner gradient bg
 *   - pink "Нестандартный слот" title
 *   - HH:MM — барабаны M.2 (решение 13.08: свайп вверх-вниз вместо
 *     степперов ▲/▼): WheelColumn с физикой эталона time-picker.js,
 *     часы 00–23, минуты шагом 5 (00–55) — семантика прежних степперов
 *   - bottom row: «ПРИМЕНИТЬ» pink + «ОТМЕНА» ghost
 *
 * Chosen value is stored in `bookings.custom_time` (TEXT, NULL when cleared).
 * Outside-click on the backdrop or Esc closes without applying.
 */
export interface CustomTimeModalProps {
  /** Initial "HH:MM" — usually the current slotTime. */
  initial: string;
  /** Apply the chosen time and close. */
  onApply: (time: string) => void;
  /** Clear custom_time back to standard slot grid. */
  onClear: () => void;
  /** Dismiss without changing. */
  onClose: () => void;
}

const HH_PATTERN = /^(\d{2}):(\d{2})$/;

function parseHM(s: string): [number, number] {
  const m = HH_PATTERN.exec(s);
  if (!m) return [12, 0];
  const h = Math.max(0, Math.min(23, parseInt(m[1] ?? "0", 10)));
  /* Минуты — к ближайшему низшему шагу 5 (сетка колеса). */
  const min = Math.max(0, Math.min(55, Math.floor(parseInt(m[2] ?? "0", 10) / 5) * 5));
  return [h, min];
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

export function CustomTimeModal({ initial, onApply, onClear, onClose }: CustomTimeModalProps) {
  const [hour, setHour] = useState<number>(() => parseHM(initial)[0]);
  const [min, setMin] = useState<number>(() => parseHM(initial)[1]);
  const whRef = useRef<WheelHandle>(null);
  const wmRef = useRef<WheelHandle>(null);
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => pad(i)), []);
  const mins = useMemo(() => Array.from({ length: 12 }, (_, i) => pad(i * 5)), []);

  /* Esc closes without applying. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const apply = () => onApply(`${pad(hour)}:${pad(min)}`);

  return (
    <div
      className={css.overlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="presentation"
    >
      <div
        className={css.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="customTime-title"
      >
        <div className={css.body}>
          <h2 id="customTime-title" className={css.title}>Нестандартный слот</h2>

          <div className={css.wheels}>
            <WheelColumn
              values={hours}
              initial={pad(hour)}
              onUserChange={(v) => setHour(Number(v))}
              handleRef={whRef}
              ariaLabel="Часы"
            />
            <span className={css.colon} aria-hidden="true">:</span>
            <WheelColumn
              values={mins}
              initial={pad(min)}
              onUserChange={(v) => setMin(Number(v))}
              handleRef={wmRef}
              ariaLabel="Минуты"
            />
            <div className={css.selframe} aria-hidden="true">
              <span className={`${css.corner} ${css.tl}`} />
              <span className={`${css.corner} ${css.tr}`} />
              <span className={`${css.corner} ${css.bl}`} />
              <span className={`${css.corner} ${css.br}`} />
            </div>
          </div>

          {initial ? (
            <button type="button" className={css.clearLink} onClick={onClear}>
              Убрать нестандартное время
            </button>
          ) : null}

          <div className={css.actions}>
            <button type="button" className={`${css.btn} ${css.btnPink}`} onClick={apply}>
              ПРИМЕНИТЬ
            </button>
            <button type="button" className={`${css.btn} ${css.btnGhost}`} onClick={onClose}>
              ОТМЕНА
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
